import { DateTime } from "luxon";

import type {
  Block,
  Marker,
} from "@app/analytics/components/PlayerControls/types";
import type { BaseSessionHighlight, SessionHighlight } from "@app/common/types";
import { getRelativeTimestamp } from "@app/common/utils";

const MARKER_TYPES: SessionHighlight["type"][] = [
  "action_item",
  "key_question",
  "topic",
];

const LABEL_BY_TYPE: { [key in SessionHighlight["type"]]: string } = {
  action_item: "Action Item",
  negative: "Reaction",
  positive: "Reaction",
  key_question: "Key Question",
  topic: "Topic",
};

const COLORS_BY_TYPE: {
  [key in SessionHighlight["type"]]: {
    color: Marker["color"];
    bgcolor: Marker["bgcolor"];
  };
} = {
  action_item: {
    color: "#706CD6",
    bgcolor: "#DBDAF5",
  },
  key_question: {
    color: "#CC8634",
    bgcolor: "#FCEDDC",
  },
  topic: {
    color: "#25B3CC",
    bgcolor: "#C0ECF3",
  },
  positive: {
    color: "#19C79F",
    bgcolor: "",
  },
  negative: {
    color: "#EF4E77",
    bgcolor: "",
  },
};

export const getBlocksAndMarkersFromHighlights = (
  highlights: SessionHighlight[]
) => {
  const blocks: Block[] = [];
  const markers: Marker[] = [];

  highlights.forEach((h) => {
    if (
      h.isDeleted ||
      h.relativeStartTime == null ||
      h.relativeEndTime == null
    ) {
      return;
    }

    const markerOrBlock = {
      id: h.id,
      description: h.description,
      label: LABEL_BY_TYPE[h.type],
      startTime: h.relativeStartTime,
      endTime: h.relativeEndTime,
      ...COLORS_BY_TYPE[h.type],
    };
    if (MARKER_TYPES.includes(h.type)) {
      markers.push(markerOrBlock);
    } else {
      blocks.push(markerOrBlock);
    }
  });

  return { blocks, markers };
};

export const populateHighlights = (
  highlights: BaseSessionHighlight[],
  meetingStartTime: DateTime,
  videoOffset: number
): SessionHighlight[] =>
  highlights.map((highlight) => ({
    ...highlight,
    timestamp: highlight.startTime
      ? getRelativeTimestamp(meetingStartTime, highlight.startTime)
      : undefined,
    relativeStartTime: highlight.startTime
      ? highlight.startTime
          .diff(meetingStartTime)
          .plus({ seconds: videoOffset })
          .as("seconds")
      : undefined,
    relativeEndTime: highlight.endTime
      ? highlight.endTime
          .diff(meetingStartTime)
          .plus({ seconds: videoOffset })
          .as("seconds")
      : undefined,
  }));
